import { Button } from "@mdxui/primitives"
import { motion, useInView, useReducedMotion } from "framer-motion"
import { type ReactNode, useRef } from "react"
import { Check, ChevronRight } from 'lucide-react'
import ReactMarkdown from "react-markdown"
import { cn } from "../../lib/cn"
import { Markdown } from "../../lib/markdown"
import { CodeWindowHero } from "../code-window"

// Animation variants
const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
}

const headerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
}

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
}

const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
}

export interface FeaturesShowcaseProps {
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Array of feature steps (supports 1-5 steps) */
  features?: Array<{
    title: string
    description: string
    /** Bullet points - shown when buttons is not provided */
    bullets?: string[]
    /** Secondary button - replaces bullets when provided */
    button?: { text: string; url: string }
    /** Optional ghost "Learn more" link below bullets or buttons */
    learnMore?: { text: string; url: string }
    image?: { src: string; alt: string }
    codeWindow?: { code: string; language: string; filename?: string }
  }>
  /** Toggle between image and code window display */
  mediaType?: "image" | "code"
  className?: string
}

/**
 * FeaturesShowcase - Numbered showcase cards with images or code windows
 *
 * Shows numbered feature cards (steps) stacked vertically.
 * Each step has its own content and code window.
 * Supports 1-5 steps.
 */
const FeaturesShowcase = ({
  features = [
    {
      title: "Install the package",
      description:
        "Get started by installing MDXUI and its dependencies. Works with any React framework.",
      bullets: [
        "One command installation",
        "Zero configuration required",
        "TypeScript support built-in",
      ],
      image: { src: "/placeholders/placeholder-1.svg", alt: "Install" },
      codeWindow: {
        code: `npm install mdxui @mdxui/primitives

# or with pnpm
pnpm add mdxui @mdxui/primitives

# or with yarn
yarn add mdxui @mdxui/primitives`,
        language: "bash",
      },
    },
    {
      title: "Import your components",
      description:
        "Import the components you need. Everything is tree-shakeable so you only bundle what you use.",
      bullets: [
        "Tree-shakeable exports",
        "Full TypeScript intellisense",
        "Auto-complete for all props",
      ],
      image: { src: "/placeholders/placeholder-2.svg", alt: "Import" },
      codeWindow: {
        code: `import { Hero, Features, Pricing } from 'mdxui'
import { Button, Card, Badge } from '@mdxui/primitives'

// Use semantic components for landing pages
import { FeaturesShowcase } from '@mdxui/neo'`,
        language: "typescript",
      },
    },
    {
      title: "Build your page",
      description:
        "Compose components to build beautiful, responsive pages. Each component is fully customizable.",
      bullets: [
        "Composable component API",
        "Responsive by default",
        "Dark mode support",
      ],
      image: { src: "/placeholders/placeholder-1.svg", alt: "Build" },
      codeWindow: {
        code: `export default function LandingPage() {
  return (
    <>
      <Hero
        title="Ship faster with MDXUI"
        description="Production-ready components"
        callToAction="Get Started"
      />
      <Features variant="showcase" />
      <Pricing plans={plans} />
    </>
  )
}`,
        language: "tsx",
      },
    },
  ],
  heading = "Built for developers",
  description:
  sectionDescription = "Everything you need to build production-ready applications in three simple steps.",
  mediaType = "image",
  className,
}: FeaturesShowcaseProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : headerVariants
  const cardAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : cardVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn("py-32", className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container max-w-7xl mx-auto">
        {(heading || sectionDescription) && (
          <motion.div
            variants={headerAnimation}
            className="mx-auto mb-16 max-w-3xl text-center"
          >
            {heading && (
              <h2 className="text-3xl font-semibold lg:text-4xl">{heading}</h2>
            )}
            {sectionDescription && (
              <div className="mt-4">
                <Markdown size="lg">{sectionDescription}</Markdown>
              </div>
            )}
          </motion.div>
        )}
        <div className="space-y-10">
          {features.map((feature, index) => (
            <motion.div key={index.toString()} variants={cardAnimation}>
              <FeatureCard
                number={String(index + 1).padStart(2, "0")}
                title={feature.title}
                description={feature.description}
                bullets={feature.bullets}
                button={feature.button}
                learnMore={feature.learnMore}
                image={feature.image}
                codeWindow={feature.codeWindow}
                mediaType={mediaType}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  )
}

interface FeatureCardProps {
  number: string
  title: string
  description: string
  bullets?: string[]
  button?: { text: string; url: string }
  learnMore?: { text: string; url: string }
  image?: { src: string; alt: string }
  codeWindow?: { code: string; language: string; filename?: string }
  mediaType?: "image" | "code"
  icon?: ReactNode
}

function FeatureCard({
  number,
  title,
  description,
  bullets,
  button,
  learnMore,
  image = { src: "/placeholders/placeholder-1.svg", alt: "Feature image" },
  codeWindow,
  mediaType = "image",
  icon = <Check className="mt-0.5 size-4 shrink-0 sm:mt-1" />,
}: FeatureCardProps) {
  return (
    <div className="grid overflow-hidden rounded-[var(--radius)] border border-border md:grid-cols-2 shadow-sm bg-muted-foreground/5">
      <div className="bg-card flex h-full min-h-[300px] flex-col justify-between p-6 md:min-h-[450px] lg:p-8 xl:p-10">
        <div>
          <span className="mb-4 flex size-6 items-center justify-center rounded-[var(--radius-sm,calc(var(--radius)*0.5))] bg-accent text-foreground font-mono text-xs">
            {number}
          </span>
          <h3 className="mb-3 text-2xl font-semibold sm:mb-5 md:text-3xl">
            {title}
          </h3>
          <div className="prose prose-sm max-w-none text-muted-foreground md:prose-base prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none">
            <ReactMarkdown>{description}</ReactMarkdown>
          </div>
        </div>
        <div className="mt-6 space-y-8">
          {/* Bullets list */}
          {bullets && bullets.length > 0 && (
            <ul className="space-y-3">
              {bullets.map((bullet) => (
                <li key={bullet} className="flex gap-x-3">
                  {icon}
                  <span className="text-sm md:text-base [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm [&_code]:text-foreground">
                    <ReactMarkdown>{bullet}</ReactMarkdown>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* Optional buttons below bullets */}
          {(button || learnMore) && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              {button && (
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <a href={button.url}>{button.text}</a>
                </Button>
              )}
              {learnMore && (
                <a
                  href={learnMore.url}
                  className="group inline-flex w-full items-center justify-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground sm:w-auto sm:justify-start"
                >
                  {learnMore.text}
                  <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="relative order-first overflow-hidden max-h-80 md:order-last md:max-h-[500px] flex items-center justify-center">
        {mediaType === "code" && codeWindow ? (
          <CodeWindowHero
            code={codeWindow.code}
            language={codeWindow.language}
            showHeader={false}
            showCopy={false}
            theme="dark"
            style="borderless"
            size="full"
            className="h-full w-full p-0 sm:p-4"
          />
        ) : (
          <img
            src={image.src}
            alt={image.alt}
            className="h-full w-full object-cover"
          />
        )}
      </div>
    </div>
  )
}

export { FeaturesShowcase }
